import { useEffect } from 'react'

import { ProductV2 } from '@common'
import { StoreBonusStatusResponse } from '@common/models/bonus'

import { Game } from '@/fixtures/games'

import { useBonusStatusQuery, useFetchProductsV2Query } from '../../api'
import { Loader } from '../../components/Loader'
import { COLOR_BLACK } from '../../utils/styles'
import { useAppSelector } from '../../utils/useAppSelector'
import { useLocale } from '../locale/useLocale'
import { LOGIN_STATUS } from '../user/userSlice'
import { Header } from './_storeBase/header/Header'
import { StorefrontWrapper } from './_storeBase/structure/StorefrontWrapper'
import BrawlStarsStoreFront from './brawlstars'
import ClashOfClansStoreFront from './clashofclans'
import ClashRoyaleStoreFront from './clashroyale'

import styles from './index.module.scss'

type GameStoreFrontProps = {
  game: Game
  products: ProductV2[]
  bonusStatus?: StoreBonusStatusResponse
  serverLoginStatus: LOGIN_STATUS
}

const RehydrateLoader = () => (
  <div className={styles.LoaderContainer}>
    <Loader strokeColor={COLOR_BLACK} />
  </div>
)

export function GameStore({ game, serverLoginStatus }: GameStoreFrontProps) {
  const loginStatus = useAppSelector((state) => state.user.loginStatus)
  const locale = useLocale()
  const viewerCountry = useAppSelector((state) => state.config.viewerCountry)
  const { data: statefulBonusStatus, isLoading: bonusStatusLoading } = useBonusStatusQuery(game.slug, {
    skip: loginStatus !== LOGIN_STATUS.LOGGED_IN,
  })
  const {
    data: statefulProducts,
    isLoading: productsLoading,
    refetch,
  } = useFetchProductsV2Query({
    gameId: game.slug,
    locale,
    viewerCountry,
  })

  useEffect(() => {
    // Server had no user but had the sso cookie, sso client loggs in
    if (serverLoginStatus === LOGIN_STATUS.SSO_LOGIN_MIGHT_HAPPEN && loginStatus === LOGIN_STATUS.LOGGED_IN) {
      refetch()
    }

    // Server had user, sso client logs out
    if (loginStatus === LOGIN_STATUS.LOGGED_OUT && serverLoginStatus === LOGIN_STATUS.LOGGED_IN) {
      refetch()
    }

    // Server had no user, sso client logs in
    if (loginStatus === LOGIN_STATUS.LOGGED_IN && serverLoginStatus === LOGIN_STATUS.LOGGED_OUT) {
      refetch()
    }
  }, [serverLoginStatus, loginStatus, refetch])

  const isLoading = !statefulProducts || productsLoading || bonusStatusLoading

  return (
    <StorefrontWrapper>
      <Header />
      {isLoading ? (
        <RehydrateLoader />
      ) : (
        <GameStoreFront
          game={game}
          products={statefulProducts}
          bonusStatus={statefulBonusStatus}
          serverLoginStatus={serverLoginStatus}
        />
      )}
    </StorefrontWrapper>
  )
}

function GameStoreFront(props: GameStoreFrontProps) {
  switch (props.game.slug) {
    case 'brawlstars':
      return <BrawlStarsStoreFront {...props} />
    case 'clashofclans':
      return <ClashOfClansStoreFront {...props} />
    case 'clashroyale':
      return <ClashRoyaleStoreFront {...props} />
    default:
      return null
  }
}
